import React, { useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { HiPlus, HiCursorArrowRays, HiXMark } from 'react-icons/hi2';
import FilterDropdown from '../../../../../Menu/components/Items/components/Table/components/FilterDropdown/FilterDropdown';

interface FilterOption {
  value: string | number;
  label: string;
}

interface FilterBarProps {
  // State filter props
  showOnlyActive: boolean;
  onActiveFilterChange: (active: boolean) => void;
  
  // Stock control filter props
  showOnlyWithStock: boolean;
  onStockFilterChange: (withStock: boolean) => void;
  
  // Show only inactive filter
  showOnlyInactive: boolean;
  onInactiveFilterChange: (inactive: boolean) => void;

  // Need production filter
  showOnlyNeedProduction: boolean;
  onNeedProductionFilterChange: (needProduction: boolean) => void;

  showOnlyNoNeedProduction: boolean;
  onNoNeedProductionFilterChange: (noNeedProduction: boolean) => void;

  // Composite filter
  showOnlyComposite: boolean;
  onCompositeFilterChange: (composite: boolean) => void;

  showOnlyNonComposite: boolean;
  onNonCompositeFilterChange: (nonComposite: boolean) => void;

  // Action buttons props
  showSelection: boolean;
  selectedIngredientsCount: number;
  onCreateIngredient: () => void;
  onToggleSelection: () => void;
  onDeleteSelection?: () => void;

  // Search bar (rendered inside toolbar)
  searchBar?: React.ReactNode;

  // Mobile filter modal props
  showMobileFilters?: boolean;
  onCloseMobileFilters?: () => void;
}

const FilterBar: React.FC<FilterBarProps> = ({
  showOnlyActive,
  onActiveFilterChange,
  showOnlyWithStock,
  onStockFilterChange,
  showOnlyInactive,
  onInactiveFilterChange,
  showOnlyNeedProduction,
  onNeedProductionFilterChange,
  showOnlyNoNeedProduction,
  onNoNeedProductionFilterChange,
  showOnlyComposite,
  onCompositeFilterChange,
  showOnlyNonComposite,
  onNonCompositeFilterChange,
  showSelection,
  selectedIngredientsCount,
  onCreateIngredient,
  onToggleSelection,
  onDeleteSelection,
  searchBar,
  showMobileFilters = false,
  onCloseMobileFilters,
}) => {
  const [localShowMobileFilters, setLocalShowMobileFilters] = useState(false);

  // Use external state if provided, otherwise use local state
  const isFiltersOpen = showMobileFilters !== undefined ? showMobileFilters : localShowMobileFilters;
  const closeFilters = onCloseMobileFilters || (() => setLocalShowMobileFilters(false));

  // State filter options
  const stateOptions = [
    { value: 'active', label: 'Solo activos' },
    { value: 'inactive', label: 'Solo inactivos' }
  ];

  const stockOptions = [
    { value: 'with_stock', label: 'Con control de stock' }
  ];

  const productionOptions = [
    { value: 'need_production', label: 'Necesita producción' },
    { value: 'no_need_production', label: 'No necesita producción' }
  ];

  const compositeOptions = [
    { value: 'composite', label: 'Es compuesto' },
    { value: 'non_composite', label: 'No es compuesto' }
  ];

  // Get selected state options
  const selectedStateOptions: FilterOption[] = [];
  if (showOnlyActive) selectedStateOptions.push({ value: 'active', label: 'Solo activos' });
  if (showOnlyInactive) selectedStateOptions.push({ value: 'inactive', label: 'Solo inactivos' });

  const selectedStockOptions = showOnlyWithStock ? [{ value: 'with_stock', label: 'Con control de stock' }] : [];

  const selectedProductionOptions: FilterOption[] = [];
  if (showOnlyNeedProduction) selectedProductionOptions.push({ value: 'need_production', label: 'Necesita producción' });
  if (showOnlyNoNeedProduction) selectedProductionOptions.push({ value: 'no_need_production', label: 'No necesita producción' });

  const selectedCompositeOptions: FilterOption[] = [];
  if (showOnlyComposite) selectedCompositeOptions.push({ value: 'composite', label: 'Es compuesto' });
  if (showOnlyNonComposite) selectedCompositeOptions.push({ value: 'non_composite', label: 'No es compuesto' });

  const handleStateFilterChange = (selected: FilterOption[]) => {
    const hasActive = selected.some(opt => opt.value === 'active');
    const hasInactive = selected.some(opt => opt.value === 'inactive');
    
    onActiveFilterChange(hasActive);
    onInactiveFilterChange(hasInactive);
  };

  const handleStockFilterChange = (selected: FilterOption[]) => {
    const hasStock = selected.some(opt => opt.value === 'with_stock');
    onStockFilterChange(hasStock);
  };

  const handleProductionFilterChange = (selected: FilterOption[]) => {
    const hasNeedProduction = selected.some(opt => opt.value === 'need_production');
    const hasNoNeedProduction = selected.some(opt => opt.value === 'no_need_production');
    
    onNeedProductionFilterChange(hasNeedProduction);
    onNoNeedProductionFilterChange(hasNoNeedProduction);
  };

  const handleCompositeFilterChange = (selected: FilterOption[]) => {
    const hasComposite = selected.some(opt => opt.value === 'composite');
    const hasNonComposite = selected.some(opt => opt.value === 'non_composite');
    
    onCompositeFilterChange(hasComposite);
    onNonCompositeFilterChange(hasNonComposite);
  };

  const FilterContent = ({ isMobileOffcanvas = false }: { isMobileOffcanvas?: boolean }) => (
    <>
      {/* ROW 1: Search + Action buttons (right-aligned) */}
      <div className="flex flex-wrap items-center gap-2 lg:gap-3">
        {searchBar && (
          <div className="flex-1 min-w-[180px] lg:min-w-[220px] max-w-[380px] relative">
            {searchBar}
          </div>
        )}

        {!isMobileOffcanvas && (
          <div className="flex items-center gap-2 ml-auto">
            {showSelection && selectedIngredientsCount > 0 && onDeleteSelection && (
              <button className="mp-btn-danger" onClick={onDeleteSelection}>
                Eliminar selección ({selectedIngredientsCount})
              </button>
            )}

            <button className="mp-btn-secondary" onClick={onToggleSelection}>
              {showSelection ? (
                <><HiXMark style={{ marginRight: '0.375rem' }} /> Cancelar</>
              ) : (
                <><HiCursorArrowRays style={{ marginRight: '0.375rem' }} /> Seleccionar</>
              )}
            </button>

            <button className="mp-btn-primary" onClick={onCreateIngredient}>
              <HiPlus style={{ marginRight: '0.375rem' }} />
              Crear ingrediente
            </button>
          </div>
        )}
      </div>

      {/* ROW 2: Filter dropdowns (with border separator) */}
      <div className="flex items-center gap-2 flex-wrap mt-3 pt-3" style={{ borderTop: '1px solid var(--border-color)' }}>
        <span className="text-xs font-semibold uppercase tracking-wider opacity-35 mr-1">Filtrar:</span>

        <FilterDropdown
          options={stateOptions}
          selectedItems={selectedStateOptions}
          onChange={handleStateFilterChange}
          placeholder="Estado"
        />
        
        <FilterDropdown
          options={stockOptions}
          selectedItems={selectedStockOptions}
          onChange={handleStockFilterChange}
          placeholder="Control de Stock"
        />

        <FilterDropdown
          options={productionOptions}
          selectedItems={selectedProductionOptions}
          onChange={handleProductionFilterChange}
          placeholder="Producción"
        />

        <FilterDropdown
          options={compositeOptions}
          selectedItems={selectedCompositeOptions}
          onChange={handleCompositeFilterChange}
          placeholder="Composición"
        />
      </div>
    </>
  );

  return (
    <div className="mp-toolbar">
      {/* Desktop Inline Filters */}
      <div className="hidden md:block">
        <FilterContent />
      </div>

      {/* Mobile Offcanvas */}
      <Offcanvas
        show={isFiltersOpen}
        onHide={closeFilters}
        placement="end"
        className="mp-modal-slider"
      >
        <Offcanvas.Header>
          <div className="form-modal-title-container">
            <Offcanvas.Title className="form-modal-title">
              Filtros de Ingredientes
            </Offcanvas.Title>
          </div>
          <button className="mp-btn-icon" onClick={closeFilters}>
            <HiXMark />
          </button>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <FilterContent isMobileOffcanvas={true} />
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};

export default FilterBar;
